import React from 'react';
import { inject, observer } from 'mobx-react';
import styles from './index.module.scss';
import type { AppStore } from '@stores/app/store';
import type { SiteAction } from '@stores/site/action';
import type { UserAction } from '@stores/user/action';
import { BASE_IMG_URL } from '@images/imageUrls';
import { withRouter } from 'react-router-dom';
import { BaseComponent } from '@components/Base/h5/BaseComponent';
import type { BaseRouteComponentProps } from 'types/base';
import classNames from 'classnames';
import Toast from '@components/Toast';
import { setThemeColor } from '@utils/colorConversion';
import updateImageListSrc from "@utils/update-image-list-src";

// ==================== 类型定义 ====================

/** 奖品类型定义 */
export interface Prize {
    id: string;           // 奖品唯一标识
    name: string;         // 奖品名称
    icon: string;         // 奖品图标URL
    introduce: string;    // 奖品介绍
    stock: number;        // 奖品库存
    type: number;         // 奖品类型
    award: string;        // 奖品值
    awardLevel: number;   // 奖品等级
}

/** 抽奖结果类型 */
export interface LotteryResult {
    prizeId: string;      // 中奖奖品ID
    prizeName: string;    // 中奖奖品名称
}

/** 九宫格抽奖组件属性接口 */
interface NineGridLotteryProps extends BaseRouteComponentProps {
    app?: AppStore;                                    // 应用全局状态
    site?: SiteAction;                                 // 站点相关操作
    user?: UserAction;                                 // 用户相关操作
    lotteryData: any;                                  // 抽奖活动数据
    prizes: Prize[];                                   // 奖品列表（必须8个）
    canDraw?: boolean;                                 // 是否可抽奖
    className?: string;                                // 自定义样式类名
    onDraw: () => Promise<LotteryResult>;              // 抽奖API调用函数
    onLotteryStart?: () => void;                       // 抽奖开始回调
    onLotteryEnd?: (result: LotteryResult) => void;    // 抽奖结束回调
    onLotteryError?: (error: Error) => void;           // 抽奖错误回调
    openLotteryActivitiesDrawsList?: () => void;       // 打开中奖记录弹窗
    openLotteryActivitiesTicketsList?: () => void;     // 打开抽奖券记录弹窗
    themeColor?: any;                                  // 主题色配置
}

/** 九宫格抽奖组件状态接口 */
interface NineGridLotteryState {
    loading: boolean;           // 加载状态
    hasMore: boolean;           // 是否有更多数据
    error: any;                 // 错误信息
    pageLoaded: any;            // 页面加载状态
    isRolling: boolean;         // 是否处于抽奖流程中（请求中或转动中），用于禁用按钮
    isRequesting: boolean;      // 是否正在请求抽奖接口（点击后~接口返回前，此阶段九宫格保持静止）
    activeIndex: number | null; // 当前高亮的奖品索引（0-7），null表示无高亮
    remainingChances: number;   // 剩余抽奖次数（抽奖券数量）
    pointerRotation: number;    // 指针旋转角度
}

// ==================== 组件定义 ====================

/**
 * 九宫格抽奖组件
 * 实现了一个带有滚动抽奖动画的九宫格抽奖界面
 * 支持动态主题色、抽奖券数量显示等功能
 */
@inject('app', 'site', 'user')
@observer
class NineGridLottery extends BaseComponent<NineGridLotteryProps, NineGridLotteryState> {
    private rollTimer: any = null;          // 滚动定时器
    private rollCount: number = 0;           // 已滚动步数
    private totalRollSteps: number = 0;      // 总滚动步数
    private targetPrizeIndex: number | null = null;  // 目标奖品索引
    private currentActiveIndex: number | null = null; // 当前高亮索引
    private startTime: number = 0;           // 动画开始时间
    private animationFrame: number | null = null;     // requestAnimationFrame ID
    private isDrawing: boolean = false;      // 同步锁：整个抽奖流程（请求+转动）期间为 true，防止 setState 异步导致的重复点击

    constructor(props: NineGridLotteryProps) {
        super(props);
        this.state = {
            loading: false,
            hasMore: false,
            error: null,
            pageLoaded: false,
            isRolling: false,
            isRequesting: false,
            activeIndex: null,      // 初始无高亮
            remainingChances: 0,
            pointerRotation: 180,
        };
    }

    // ==================== 生命周期方法 ====================

    /**
     * 组件挂载后的初始化
     */
    componentDidMount() {
        this.validatePrizes();      // 验证奖品列表配置
        this.initUserData();        // 初始化用户抽奖券数据
        let { themeColor } = this.props;
        if (themeColor) {
            setThemeColor(themeColor, 'lottery-primary-color');       // 设置主题色CSS变量
            setThemeColor(themeColor, 'lottery-primary-color-rgb');   // 设置RGB主题色CSS变量
        }
    }

    /**
     * 组件更新时的处理
     * 监听奖品列表和抽奖数据变化
     */
    componentDidUpdate(prevProps: NineGridLotteryProps) {
        if (prevProps.prizes !== this.props.prizes) {
            this.validatePrizes();    // 奖品列表变化时重新验证
        }
        // 监听 lotteryData 变化，更新抽奖券数量
        if (prevProps.lotteryData?.user?.remainingTickets !== this.props.lotteryData?.user?.remainingTickets) {
            this.initUserData();       // 抽奖券数量变化时更新
        }
    }

    /**
     * 组件卸载时清理定时器和动画
     */
    componentWillUnmount() {
        if (this.rollTimer) {
            clearTimeout(this.rollTimer);
            this.rollTimer = null;
        }
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
    }

    // ==================== 私有方法 ====================

    /**
     * 验证奖品列表配置
     * 九宫格抽奖必须恰好包含8个奖品（中间为抽奖按钮区域）
     */
    validatePrizes = () => {
        const { prizes } = this.props;
        if (!prizes || prizes.length !== 8) {
            console.error('NineGridLottery: 奖品列表必须包含8个奖品，当前数量：', prizes?.length);
        }
    }

    /**
     * 初始化用户抽奖数据
     * 根据用户的抽奖券数量初始化剩余抽奖次数
     */
    initUserData = () => {
        const { lotteryData } = this.props;
        const { isRolling } = this.state;
        // 从 lotteryData 中获取用户的抽奖券数量
        const totalTickets = lotteryData?.user?.remainingTickets || 0;
        
        // 如果正在滚动中，不要重置滚动相关状态，避免中断动画
        if (isRolling) {
            this.setState({
                remainingChances: totalTickets > 0 ? totalTickets : 0,
            });
            return;
        }
        
        this.setState({
            remainingChances: totalTickets > 0 ? totalTickets : 0,
            // 重置状态：无高亮，指针归位（向下180度）
            activeIndex: null,
            pointerRotation: 180,
            isRolling: false,
            isRequesting: false,
        });
        
        // 重置动画相关变量
        this.rollCount = 0;
        this.totalRollSteps = 0;
        this.targetPrizeIndex = null;
        this.currentActiveIndex = null;
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
    }

    /**
     * 根据角度获取当前奖品索引
     * @param angle - 旋转角度（度）
     * @returns 奖品索引（0-7）
     */
    getCurrentIndexByAngle = (angle: number): number => {
        // 归一化角度到0-360
        const normalizedAngle = ((angle % 360) + 360) % 360;
        // 加上45度校正（指针icon5.png初始有-45度偏移），然后除以45取整
        const correctedAngle = (normalizedAngle + 45) % 360;
        let index = Math.floor(correctedAngle / 45) % 8;
        return index;
    }

    /**
     * 计算指定奖品索引对应的最终旋转角度
     * @param prizeIndex - 奖品索引（0-7）
     * @returns 旋转角度（度）
     */
    calculateFinalAngle = (prizeIndex: number): number => {
        // 每个奖品占45度，指针角度按照0, 45, 90度等以此类推
        // 奖品0（上）对应角度：0度
        // 奖品1（右上）对应角度：45度
        // 奖品2（右）对应角度：90度
        // 以此类推...
        // 减去45度校正，因为icon5.png初始指向有45度偏移
        const centerAngle = prizeIndex * 45 - 45;
        // 归一化到0-360度
        return ((centerAngle % 360) + 360) % 360;
    }

    /**
     * 重置抽奖流程状态（接口失败或返回无效时调用）
     * 只复位状态、不移动九宫格指针，确保接口失败时转盘停留在初始静止位置，
     * 避免出现"停在大奖格"引起争议
     */
    resetDrawState = () => {
        this.isDrawing = false;
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
        this.setState({ isRolling: false, isRequesting: false });
    };

    /**
     * 开始滚动抽奖动画
     * @param finalPrizeIndex - 最终停留的奖品索引（0-7）
     * 
     * 动画逻辑：
     * 1. 根据当前指针角度计算当前所在奖品索引
     * 2. 计算滚动圈数（6-10圈随机）
     * 3. 计算到达目标奖品所需步数
     * 4. 使用 requestAnimationFrame 实现丝滑动画
     * 5. 应用 ease-in-out 缓动函数使动画先快后慢
     */
    startRoll = (finalPrizeIndex: number) => {
        const { pointerRotation } = this.state;
        
        // 根据当前指针角度获取当前奖品索引
        const currentIdx = this.getCurrentIndexByAngle(pointerRotation);
        
        // 更新 activeIndex 与指针角度同步
        this.setState({ activeIndex: currentIdx });
        
        // 随机滚动圈数：6～10圈
        const rounds = Math.floor(Math.random() * 5) + 6;
        // 计算从当前位置到目标位置需要经过的步数（顺时针）
        const stepsToTarget = (finalPrizeIndex - currentIdx + 8) % 8;
        // 总滚动步数 = 圈数 * 8 + 到达目标的步数
        const totalSteps = rounds * 8 + stepsToTarget;
        
        this.totalRollSteps = totalSteps;
        this.rollCount = 0;
        this.targetPrizeIndex = finalPrizeIndex;
        this.currentActiveIndex = currentIdx;
        this.startTime = Date.now();
        
        // 清除之前的定时器和动画
        if (this.rollTimer) {
            clearTimeout(this.rollTimer);
            this.rollTimer = null;
        }
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
        
        // 使用 requestAnimationFrame 实现丝滑动画
        const animate = () => {
            const elapsed = Date.now() - this.startTime;
            
            // 总动画时长4秒
            const duration = 4000;
            let progress = Math.min(1, elapsed / duration);
            
            // ease-in-out 缓动函数：先慢 -> 快 -> 慢
            const easeProgress = progress < 0.5 
                ? 2 * progress * progress 
                : 1 - Math.pow(-2 * progress + 2, 2) / 2;
            
            // 根据进度计算应该完成的步数
            let targetStep = Math.floor(easeProgress * this.totalRollSteps);
            
            // 如果超过总步数，结束动画
            if (targetStep >= this.totalRollSteps) {
                this.stopRoll();
                return;
            }
            
            // 如果步数有变化，更新高亮索引和指针旋转角度
            if (targetStep > this.rollCount) {
                this.rollCount = targetStep;
                
                // 计算旋转角度：起始角度 + 步数 * 45度
                const rotationAngle = (pointerRotation + this.rollCount * 45) % 360;
                
                // 根据角度计算当前高亮的奖品索引，确保与指针位置同步
                this.currentActiveIndex = this.getCurrentIndexByAngle(rotationAngle);
                
                this.setState({ 
                    activeIndex: this.currentActiveIndex,
                    pointerRotation: rotationAngle
                });
            }
            
            this.animationFrame = requestAnimationFrame(animate);
        };
        
        this.animationFrame = requestAnimationFrame(animate);
    };

    /**
     * 停止滚动动画
     * 清理动画资源并触发抽奖结束回调
     */
    stopRoll = () => {
        // 取消动画帧
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
        
        // 确保指针最终停在目标奖品对应的精确角度
        if (this.targetPrizeIndex !== null) {
            // 计算目标奖品对应的最终角度
            const finalAngle = this.calculateFinalAngle(this.targetPrizeIndex);
            
            this.setState({ 
                isRolling: false,
                pointerRotation: finalAngle,
                activeIndex: this.targetPrizeIndex
            });
            
            // 触发抽奖结束回调，传递中奖结果
            const { prizes, onLotteryEnd, lotteryData } = this.props;
            const wonPrize = prizes[this.targetPrizeIndex];
            
            const result: LotteryResult = {
                prizeId: wonPrize.id,
                prizeName: wonPrize.name,
            };
            
            // 抽奖结束后，抽奖券数量减1
            const currentTickets = lotteryData?.user?.remainingTickets || 0;
            this.setState({
                remainingChances: Math.max(0, currentTickets - 1)
            });
            
            onLotteryEnd?.(result);
        } else {
            this.setState({ isRolling: false });
        }
        
        // 重置滚动相关状态（保留 pointerRotation 和 activeIndex）
        this.rollCount = 0;
        this.totalRollSteps = 0;
        this.targetPrizeIndex = null;
        this.currentActiveIndex = null;
        // 释放同步锁，允许下一次抽奖
        this.isDrawing = false;
    };

    /**
     * 处理抽奖点击事件
     * 流程：校验条件 -> 进入"请求中"状态（按钮 loading，九宫格静止不动）
     *      -> 调用抽奖接口 -> 接口成功才启动转动动画并定位到中奖格
     *      -> 接口失败/返回无效仅 Toast 提示，九宫格全程保持静止，绝不会停在某个奖品格
     */
    handleLotteryClick = async () => {
        
        const { isRolling, isRequesting, remainingChances } = this.state;
        const { canDraw = true, prizes, lotteryData, onDraw, onLotteryStart } = this.props;
        // 检查活动状态是否正常
        if (lotteryData?.status == 0) {
            Toast?.info?.({ content: '抽奖活动还未开始' });
            return;
        }
        // 检查活动状态是否正常
        if (lotteryData?.status == 3) {
            Toast?.info?.({ content: '抽奖活动已结束' });
            return;
        }

        // 抽奖流程进行中（请求中或转动中），禁止重复点击（isDrawing 为同步锁，规避 setState 异步）
        if (this.isDrawing || isRolling || isRequesting) {
            Toast?.info?.({ content: '抽奖中，请稍后...' });
            return;
        }
        
        // 检查是否可抽奖
        if (!canDraw) {
            Toast?.info?.({ content: '暂无法抽奖' });
            return;
        }
        
        // // 检查抽奖券是否充足
        if (remainingChances <= 0) {
            Toast?.info?.({ content: '抽奖券用完啦，参与任务轻松赚取~' });
            return;
        }

        // 调用父组件的抽奖方法
        if (!onDraw) {
            console.error('onDraw 方法未定义');
            Toast?.info?.({ content: '抽奖功能未配置' });
            return;
        }

        // 进入"请求中"状态：按钮 loading/置灰，九宫格保持静止（不启动任何转动动画）
        this.isDrawing = true;
        this.setState({ isRolling: true, isRequesting: true });
        onLotteryStart?.();
        
        try {
            // 调用抽奖API，等待接口返回后再决定是否转动
            const result = await onDraw();
            console.log('子组件收到抽奖结果:', result);
            
            if (!result || !result.prizeId) {
                console.error('抽奖返回数据无效, prizeId缺失', result);
                Toast?.info?.({ content: '当前活动人气爆棚，请稍后再试~' });
                this.resetDrawState();
                return;
            }
            
            // 查找中奖奖品在列表中的索引
            const prizeIndex = prizes.findIndex(p => p.id === result.prizeId);
            
            if (prizeIndex === -1) {
                console.error('中奖奖品不在配置列表中', result.prizeId);
                Toast?.info?.({ content: '当前活动人气爆棚，请稍后再试~' });
                this.resetDrawState();
                return;
            }
            
            // 接口成功：结束"请求中"状态，启动定位减速动画，最终停在中奖奖品位置
            this.setState({ isRequesting: false });
            this.startRoll(prizeIndex);
        } catch (error) {
            console.error('抽奖失败', error);
            // network: 断网/弱网/超时；service: 后端业务/服务异常。默认按服务异常处理
            const errorType = (error as any)?.errorType;
            const tip = errorType === 'network'
                ? '网络状况不佳，请检查网络后再试~'
                : '当前活动人气爆棚，请稍后再试~';
            Toast?.info?.({ content: tip, showIcon: errorType !== 'network' });
            this.resetDrawState();
            this.props.onLotteryError?.(error as Error);
        }
    };

    /**
     * 渲染组件内容
     * 构建九宫格布局，包含奖品格子、抽奖按钮、中奖记录入口等
     */
    renderContent() {
        const { isRolling, isRequesting, activeIndex, remainingChances, pointerRotation } = this.state;
        const { lotteryData, className, prizes, themeColor, openLotteryActivitiesDrawsList, openLotteryActivitiesTicketsList, user } = this.props;
        const isLoggedIn = !!user?.userInfo?.code;
        
        /**
         * 九宫格布局位置映射（顺时针顺序）
         * 索引对应关系：
         * 0: 上     1: 右上   2: 右
         * 3: 左上   4: 中间   5: 右下
         * 6: 左     7: 左下   8: 下
         */
        const gridItems = [
            prizes[0],  // 上
            prizes[1],  // 右上
            prizes[2],  // 右
            prizes[7],  // 左上
            null,       // 中间（抽奖按钮区域）
            prizes[3],  // 右下
            prizes[6],  // 左
            prizes[5],  // 左下
            prizes[4],  // 下
        ];
        
        /**
         * 将网格位置转换为实际的奖品索引
         * @param gridPosition - 网格位置（0-8，排除4）
         * @returns 奖品索引（0-7）
         */
        const getActualIndex = (gridPosition: number): number => {
            const mapping: Record<number, number> = {
                0: 0, 1: 1, 2: 2, 3: 7, 5: 3, 6: 6, 7: 5, 8: 4
            };
            return mapping[gridPosition] ?? -1;
        };
        
        /**
         * 将奖品索引转换为网格位置
         * @param prizeIndex - 奖品索引（0-7）
         * @returns 网格位置（0-8）
         */
        const getGridPosition = (prizeIndex: number): number => {
            const mapping: Record<number, number> = {
                0: 0, 1: 1, 2: 2, 3: 5, 4: 8, 5: 7, 6: 6, 7: 3
            };
            return mapping[prizeIndex] ?? -1;
        };
        
        return (
            <div className={classNames(styles.nineGridWrapper, className)}>
                {/* 九宫格容器 */}
                <div className={styles.nineGridContainer}>
                    {gridItems.map((item, idx) => {
                        const isCenter = idx === 4;  // 中间位置是抽奖按钮
                        const actualIndex = !isCenter ? getActualIndex(idx) : -1;
                        // 只有当 activeIndex 不为 null 且与当前格子索引匹配时才高亮
                        const isActive = !isCenter && activeIndex !== null && activeIndex === actualIndex;
                        const itemKey = isCenter ? 'center' : (item?.id || `prize-${idx}`);
                        
                        return (
                            <div
                                key={itemKey}
                                className={classNames(styles.gridItem, {
                                    [styles.isRolling]: isRolling,  // 滚动中的样式
                                    [styles.active]: isActive,       // 高亮样式
                                    [styles.centerPointer]: isCenter, // 中间指针样式
                                })}
                            >
                                {!isCenter ? (
                                    // 奖品格子内容
                                    <>
                                        {/* 奖品背景图 */}
                                        <img 
                                            src={`${BASE_IMG_URL}/lottery/bg2.png`} 
                                            alt="背景" 
                                            className={styles.gridItemBg}
                                        />
                                        <div className={styles.prizeImgWrapper}>
                                            {/* 奖品图标 */}
                                            <img 
                                                src={updateImageListSrc(item.icon, 21)}
                                                alt="奖品" 
                                                className={styles.prizeImg}
                                            />
                                            {/* 奖品名称 */}
                                            <div className={styles.prizeName}>{item.name}</div>
                                        </div>
                                    </>
                                ) : (
                                    // 中间抽奖按钮区域
                                    <div className={styles.pointerWrapper}>
                                        {/* 指针画板（旋转的底层容器） */}
                                        <div className={styles.pointerCanvas}
                                            style={{
                                                transform: `rotate(${pointerRotation % 360}deg)`,
                                                transition: 'none',
                                        }}></div>
                                        {/* 指针图标 */}
                                        <img 
                                            src={`${BASE_IMG_URL}/lottery/icon5.png`} 
                                            alt="指针" 
                                            className={styles.pointerIcon}
                                            style={{
                                                transform: `rotate(${pointerRotation % 360}deg)`,
                                                transition: 'none'
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                
                {/* 底部操作栏 */}
                <div className={styles.lotteryButtonWrapper}>
                    {/* 中奖记录入口 */}
                    <div className={styles.winningRecordEnter} onClick={() => {
                        openLotteryActivitiesDrawsList && openLotteryActivitiesDrawsList()
                    }}>
                        <img 
                            src={`${BASE_IMG_URL}/lottery/icon6.png`} 
                            alt="中奖记录"
                            className={styles.icon} 
                        />
                        <div className={styles.text}>中奖记录</div>
                    </div>
                    
                    {/* 抽奖主按钮 */}
                    <div 
                        className={classNames(styles.lotteryButton, {
                            [styles.lotteryButtonDisabled]: isLoggedIn && (isRolling || isRequesting || lotteryData?.status != 1 || remainingChances == 0)
                        })}
                        style={{background: themeColor}}
                        onClick={() => {
                            if (!isLoggedIn) {
                                if (user && typeof user.checkLogin === 'function') {
                                    user.checkLogin('h5');
                                }
                                return;
                            }
                            // 抽奖流程进行中（请求中或转动中）禁止点击，防止重复触发
                            if (isRolling || isRequesting) {
                                return;
                            }
                            this.handleLotteryClick();
                        }}
                    >
                        {!isLoggedIn ? (
                            <div className={styles.rollingText}>请先登录</div>
                        ) : lotteryData?.status > 1 ? <div className={styles.rollingText}>抽奖活动已结束</div> : <>
                            {isRequesting ? (
                            <div className={styles.rollingText}>
                                抽奖中...
                            </div>
                        ) : isRolling ? (
                            <div className={styles.rollingText}>
                                锦鲤附体
                            </div>
                        ) : (
                            <>
                                点击抽奖<br/>
                                <span>1抽奖券/次</span>
                            </>
                        )}
                        </>}
                        
                    </div>
                    
                    {/* 抽奖券数量及记录入口 */}
                    <div className={styles.chanceTip} onClick={() => {
                        openLotteryActivitiesTicketsList && openLotteryActivitiesTicketsList()
                    }}>
                        <div className={styles.iconBox}>
                            <img 
                                src={`${BASE_IMG_URL}/lottery/icon7.png`} 
                                alt="抽奖券"
                                className={styles.icon} 
                            />
                            <div className={styles.num}>{!isLoggedIn ? '?' : remainingChances}</div>
                        </div>
                        <div className={styles.text}>抽奖券</div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        return this.renderContent();
    }
}

export default withRouter(NineGridLottery);
